import { Bell, Blocks, ChevronRight, CircleUserRound, Hammer, LogOut, Menu, MessageSquare, MoreHorizontal, Plus, ShieldCheck, X } from "lucide-react";
import { Link, NavLink, Outlet, useLocation } from "react-router";
import { Brand } from "@/components/brand";
import { APP_VERSION } from "@/app-version";
import { ActivityTracker } from "@/components/activity-tracker";
import { ThemeToggle } from "@/components/theme-toggle";
import { NotificationInbox } from "@/components/notification-inbox";
import { Button } from "@/components/ui/button";
import { useAppData } from "@/data/data-context";
import { cn } from "@/lib/utils";
import { builderGuideEnabled } from "@/pages/build-guide-model";
import { useUiStore } from "@/store/ui";
import { useMobileInterface } from "@/lib/mobile";

const links = [
  { to: "/apps", label: "App catalog", icon: Blocks },
  ...(builderGuideEnabled ? [{ to: "/build", label: "Build an app", icon: Hammer }] : []),
  { to: "/submit", label: "Submit an app", icon: Plus },
  { to: "/settings/api-access", label: "API access", icon: ShieldCheck },
];

export function AppShell() {
  const { currentUser, signOut, mode } = useAppData();
  const { sidebarOpen, setSidebarOpen } = useUiStore();
  const location = useLocation();
  const mobile = useMobileInterface();

  if (mobile) return <MobileShell />;

  return (
    <div className="min-h-screen bg-background">
      <ActivityTracker />
      <aside className={cn("fixed inset-y-0 left-0 z-40 flex w-64 flex-col bg-sidebar text-sidebar-foreground transition-transform lg:translate-x-0", sidebarOpen ? "translate-x-0" : "-translate-x-full")}>
        <div className="border-b border-white/15 px-5 py-5">
          <div className="flex items-center justify-between">
          <Brand showVersion={false} className="text-white" />
          <Button variant="ghost" size="icon" className="text-white lg:hidden" onClick={() => setSidebarOpen(false)}><X className="size-5" /><span className="sr-only">Close menu</span></Button>
          </div>
          <p className="mt-3 text-xs leading-relaxed text-white/65">Bellarmine Robotics<br />FRC Team 254</p>
        </div>
        <nav className="flex-1 space-y-1 p-3" aria-label="Primary navigation">
          {links.map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} onClick={() => setSidebarOpen(false)} className={({ isActive }) => cn("flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-bold text-white/72 transition hover:bg-white/9 hover:text-white", isActive && "bg-primary text-primary-foreground shadow-sm")}>
              <Icon className="size-4.5" />
              <span>{label}</span>
            </NavLink>
          ))}
          {<NavLink to="/feedback" onClick={() => setSidebarOpen(false)} className={({ isActive }) => cn("flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-bold text-white/72 hover:bg-white/9 hover:text-white", isActive && "bg-primary text-primary-foreground")}><Blocks className="size-4.5" />Feedback</NavLink>}
          {currentUser?.role === "admin" && (
            <NavLink to="/admin" onClick={() => setSidebarOpen(false)} className={({ isActive }) => cn("flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-bold text-white/72 transition hover:bg-white/9 hover:text-white", isActive && "bg-primary text-primary-foreground")}>
              <ShieldCheck className="size-4.5" /> Administration
            </NavLink>
          )}
        </nav>
        <div className="border-t border-white/15 p-4">
          <div className="mb-3 flex items-center gap-3">
            <div className="flex size-9 items-center justify-center rounded-full bg-white/12"><CircleUserRound className="size-5" /></div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-bold">{currentUser?.name ?? "Cheesy user"}</p>
              <p className="truncate text-[11px] text-white/55">{currentUser?.email}</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" className="w-full justify-start text-white/65 hover:bg-white/9 hover:text-white" onClick={() => void signOut()}><LogOut className="size-4" /> Sign out</Button>
          <p className="mt-3 px-3 text-[10px] tracking-wide text-white/55">Version {APP_VERSION}</p>
        </div>
      </aside>

      {sidebarOpen && <button className="fixed inset-0 z-30 bg-black/45 lg:hidden" onClick={() => setSidebarOpen(false)} aria-label="Close navigation overlay" />}

      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 flex h-16 items-center border-b border-border bg-background/88 px-4 backdrop-blur-xl sm:px-7">
          <Button variant="ghost" size="icon" className="mr-2 lg:hidden" onClick={() => setSidebarOpen(true)}><Menu className="size-5" /><span className="sr-only">Open menu</span></Button>
          <div className="flex min-w-0 items-center gap-2 text-sm text-muted-foreground">
            <span>Cheesy Apps</span><ChevronRight className="size-3.5" /><strong className="truncate text-foreground">{pageLabel(location.pathname)}</strong>
          </div>
          <div className="ml-auto flex shrink-0 items-center gap-1">
            {mode === "demo" && <span className="mr-2 hidden rounded-full bg-orange/18 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-warning-foreground sm:inline">Demo data</span>}
            <NotificationInbox />
            <ThemeToggle />
          </div>
        </header>
        <main><Outlet /></main>
      </div>
    </div>
  );
}

function MobileShell() {
  const { notifications } = useAppData();
  const location = useLocation();
  const launching = /^\/apps\/[^/]+\/launch$/.test(location.pathname);
  const unread = notifications.filter((item) => !item.read).length;
  const tabs = [
    { to: "/apps", label: "Apps", icon: Blocks },
    { to: "/feedback", label: "Feedback", icon: MessageSquare },
    { to: "/notifications", label: "Inbox", icon: Bell },
    { to: "/more", label: "More", icon: MoreHorizontal },
  ];
  return <div className={cn("mobile-shell", launching && "mobile-launch-shell")} data-mobile-interface>
    <ActivityTracker />
    {!launching && <header className="mobile-header"><strong className="font-display">Cheesy Apps</strong><span className="text-sm text-muted-foreground">{pageLabel(location.pathname)}</span></header>}
    <main className="min-w-0"><Outlet /></main>
    {!launching && <nav className="mobile-tabs" aria-label="Primary navigation">
      {tabs.map(({ to, label, icon: Icon }) => {
        const feedback = location.pathname.startsWith("/feedback") || location.pathname.endsWith("/feedback");
        const active = to === "/more"
          ? !["/apps", "/feedback", "/notifications"].some((prefix) => location.pathname.startsWith(prefix))
          : to === "/feedback" ? feedback : to === "/apps" ? location.pathname.startsWith(to) && !feedback : location.pathname.startsWith(to);
        return <Link key={to} to={to} className={cn("mobile-tab", active && "mobile-tab-active")} aria-current={active ? "page" : undefined} aria-label={to === "/notifications" && unread > 0 ? `Inbox, ${unread} unread` : undefined}>
          <span className="relative"><Icon className="size-5" />{to === "/notifications" && unread > 0 && <span className="mobile-unread" aria-hidden="true">{unread > 99 ? "99+" : unread}</span>}</span>
          <span>{label}</span>
        </Link>;
      })}
    </nav>}
  </div>;
}

function pageLabel(path: string) {
  if (path.startsWith("/settings/api-access")) return "API access";
  if (path.startsWith("/more")) return "More";
  if (path.startsWith("/feedback") || path.endsWith("/feedback")) return "Feedback";
  if (path.startsWith("/admin")) return "Administration";
  if (path.startsWith("/build")) return "Build an app";
  if (path.startsWith("/submit")) return "Submit an app";
  if (path.startsWith("/notifications")) return "Notifications";
  if (path.includes("/launch")) return "Launcher";
  if (path.startsWith("/manage")) return "App management";
  return "Catalog";
}
